import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sun, Moon, LogOut } from 'lucide-react';
import { useAuth } from '../../packages/auth-client';
import { resolveAvatarUrl } from '../../packages/api-client';
import { getProjects, type Project } from '../../packages/api-client/pm-api';
import { useTheme } from '../../hooks/useTheme';
import { KaziAtlasLogo } from '../KaziAtlasLogo';
import { isItemActive, useNavGroups, type NavItem } from './nav';
import { pinsKey, readPins } from '../../pages/tools/ToolsPage';

/**
 * Project shortcuts (spec §2): the newest accessible projects, in the server's
 * order, plus All projects. GET /projects is already scoped to the caller.
 * Nothing until the list loads; on error only the All projects link.
 */
function ProjectShortcuts({ projects, current, onNavigate }: {
    projects: Project[] | undefined; current?: string; onNavigate?: () => void;
}) {
    return (
        <section className="sidebar-section sidebar-section--projects" aria-labelledby="nav-group-projects" data-nav-group="projects">
            <h2 id="nav-group-projects" className="sidebar-section-label">Your projects</h2>
            <ul className="sidebar-list">
                {(projects ?? []).map((p) => (
                    <li key={p.id}>
                        <Link
                            to={`/projects/${p.id}`}
                            className={`sidebar-item sidebar-project${p.id === current ? ' active' : ''}`}
                            aria-current={p.id === current ? 'page' : undefined}
                            title={p.name}
                            onClick={onNavigate}
                        >
                            <span className="sidebar-project-dot" style={{ background: p.color || 'var(--accent)' }} aria-hidden="true" />
                            <span className="sidebar-project-name">{p.name}</span>
                        </Link>
                    </li>
                ))}
                <li>
                    <Link to="/projects" className="sidebar-item sidebar-project" onClick={onNavigate}>All projects</Link>
                </li>
            </ul>
        </section>
    );
}

// Concept grouping (founder concept renderNav): a primary "Workspace" nav, then
// a "Tools and settings" utility nav, then Your projects. The individual tools
// (Workload, Timeline, Records, …) are reached from Workspace tools; the ones a
// viewer pinned there are listed under it. Routes and visibility rules are nav.ts's.
const UTILITY = new Set(['tools', 'account']);

/** The grouped workspace links. Shared by the desktop sidebar and the mobile drawer. */
export function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
    const groups = useNavGroups();
    const { pathname, search } = useLocation();
    const { user } = useAuth();
    // Same gate as the Projects item.
    const canSeeProjects = groups.some((g) => g.items.some((i) => i.to === '/projects'));
    const shortcuts = useQuery({
        queryKey: ['projects', 'nav-shortcuts', user?.user_id],
        queryFn: async () => (await getProjects(1, 5)).data ?? [],
        enabled: canSeeProjects,
        staleTime: 60_000,
    });
    const currentProject = pathname.match(/^\/projects\/([^/]+)/)?.[1];
    // Exactly one current link: a matching shortcut takes it from the Projects item.
    const shortcutIsCurrent = !!currentProject && !!shortcuts.data?.some((p) => p.id === currentProject);

    const pins = readPins(pinsKey(user?.user_id ?? ''));
    const primary: NavItem[] = [];
    const utility: NavItem[] = [];
    for (const g of groups) {
        if (g.id === 'platform') continue;
        for (const item of g.items) {
            const isTool = g.id === 'tools' && item.to !== '/tools';
            if (isTool && !pins.includes(item.to)) continue;
            // Billing sits with the utility links in the concept.
            (UTILITY.has(g.id) || item.to === '/billing' ? utility : primary).push(item);
        }
    }
    // Workspace tools, its pinned tools, Billing, then Settings & help.
    const rank = (i: NavItem) => (i.to === '/tools' ? 0 : groups.find((g) => g.id === 'tools')?.items.includes(i) ? 1 : i.to === '/billing' ? 2 : 3);
    utility.sort((a, b) => rank(a) - rank(b));
    const platform = groups.find((g) => g.id === 'platform');

    const link = (item: NavItem, pinned = false) => {
        const active = isItemActive(item, pathname, search) && !(item.to === '/projects' && shortcutIsCurrent);
        const Icon = item.icon;
        return (
            <li key={item.to}>
                <Link
                    to={item.to}
                    className={`sidebar-item${pinned ? ' sidebar-item--pinned' : ''}${active ? ' active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    onClick={onNavigate}
                >
                    <Icon size={18} aria-hidden="true" /> {item.label}
                </Link>
            </li>
        );
    };
    return (
        <>
            {primary.length > 0 && (
                <nav className="sidebar-nav sidebar-nav--primary" aria-label="Workspace">
                    <ul className="sidebar-list">{primary.map((i) => link(i))}</ul>
                </nav>
            )}
            <nav className="sidebar-nav sidebar-nav--utility" aria-label="Tools and settings">
                <ul className="sidebar-list">{utility.map((i) => link(i, rank(i) === 1))}</ul>
                {platform && (
                    <section className="sidebar-section sidebar-section--platform" aria-labelledby="nav-group-platform">
                        <h2 id="nav-group-platform" className="sidebar-section-label">{platform.label}</h2>
                        <ul className="sidebar-list">{platform.items.map((i) => link(i))}</ul>
                    </section>
                )}
            </nav>
            {canSeeProjects && !shortcuts.isPending && (
                <ProjectShortcuts projects={shortcuts.data} current={currentProject} onNavigate={onNavigate} />
            )}
        </>
    );
}

/** Signed-in identity, theme control and sign-out. */
export function AccountPanel({ onNavigate }: { onNavigate?: () => void }) {
    const { user, logout } = useAuth();
    const { theme, toggle: toggleTheme } = useTheme();

    const [avatarCacheBust, setAvatarCacheBust] = useState(() => Date.now());
    // A stored avatar_url can point at a file that no longer exists (e.g. saved
    // to ephemeral storage pre-S3 and lost on redeploy → 302→403). Track load
    // failure so we render the initials fallback instead of a broken image.
    const [avatarFailed, setAvatarFailed] = useState(false);
    const prevAvatarRef = React.useRef(user?.avatar_url);
    if (prevAvatarRef.current !== user?.avatar_url) {
        prevAvatarRef.current = user?.avatar_url;
        setAvatarCacheBust(Date.now());
        setAvatarFailed(false);
    }
    const avatarSrc = useMemo(() => {
        const resolved = resolveAvatarUrl(user?.avatar_url);
        return resolved ? `${resolved}?t=${avatarCacheBust}` : undefined;
    }, [user?.avatar_url, avatarCacheBust]);

    const initials = `${user?.first_name?.charAt(0) ?? ''}${user?.last_name?.charAt(0) ?? ''}` || '?';
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
    const roles = user?.roles ?? [];

    return (
        <div className="sidebar-footer">
            <Link to="/profile" className="sidebar-account" onClick={onNavigate}>
                {user?.avatar_url && !avatarFailed ? (
                    <img src={avatarSrc} alt="" className="sidebar-avatar" onError={() => setAvatarFailed(true)} />
                ) : (
                    <span className="sidebar-avatar sidebar-avatar--initials" aria-hidden="true">{initials}</span>
                )}
                <span className="sidebar-account-text">
                    {name && <span className="sidebar-account-name">{name}</span>}
                    <span className="sidebar-account-email">{user?.email}</span>
                    {roles.length > 0 && <span className="sidebar-account-roles">{roles.join(' · ')}</span>}
                </span>
            </Link>
            <button type="button" className="sidebar-item sidebar-action" onClick={toggleTheme}>
                {theme === 'dark' ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
                {theme === 'dark' ? 'Daylight mode' : 'Dark mode'}
            </button>
            <button type="button" className="sidebar-item sidebar-action" onClick={() => logout()}>
                <LogOut size={16} aria-hidden="true" /> Sign out
            </button>
        </div>
    );
}

/** Desktop sidebar; hidden at ≤768px, where MobileDrawer carries the same content. */
export function Sidebar() {
    return (
        <aside className="sidebar">
            <div className="sidebar-logo">
                {/* The sidebar is navy in both themes. */}
                <KaziAtlasLogo size={46} />
            </div>
            <div className="sidebar-navs">
                <NavLinks />
            </div>
            <AccountPanel />
        </aside>
    );
}
